import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { removeSelectedItem, replaceStatusChangedItem } from '@/helpers/CRUD';
import { extractError } from '@/helpers/extractApiError';
import CustomerService from '@/services/CustomersServices/CustomerService';
import { deleteCustomer } from '../delete/deleteSlice';

const model = {
    data: [],
    totalItems: 0,
    loading: false,
    error: null,
    successOnCustomerStatusChange: '',
    errorOnCustomerStatusChange: null,
    mismatch_file: null,
    successOnCustomerUpload: '',
    errorOnCustomerUpload: null,
    xlsxUrl: null,
};

export const getCustomerList = createAsyncThunk(
    'customer/getCustomerList',
    async (data, { rejectWithValue }) => {
        try {
            const response = await CustomerService.getCustomerList(data);
            return response.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    },
);

export const changeCustomerStatus = createAsyncThunk(
    'customer/changeCustomerStatus',
    async (data, { rejectWithValue }) => {
        try {
            const response = await CustomerService.changeCustomerStatus(data);
            return response.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    },
);

export const downloadCustomerData = createAsyncThunk(
    'customer/downloadCustomerData',
    async (data, { rejectWithValue }) => {
        try {
            const response = await CustomerService.downloadCustomerData(data);
            const { file_url: xlsxUrl } = response.data;
            return xlsxUrl;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    },
);

export const uploadCustomerData = createAsyncThunk(
    'customer/uploadCustomerData',
    async (formData, { rejectWithValue }) => {
        try {
            const response = await CustomerService.uploadCustomerData(formData);
            return response.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    },
);

const customerListSlice = createSlice({
    name: 'customerList',
    initialState: model,
    reducers: {
        resetChangeCustomerStatusStateOnSuccess: (state) => {
            state.successOnCustomerStatusChange = '';
        },
        resetChangeCustomerStatusStateOnError: (state) => {
            state.errorOnCustomerStatusChange = null;
        },
        resetCustomerUploadStateOnSuccess: (state) => {
            state.successOnCustomerUpload = '';
        },
        resetCustomerUploadStateOnError: (state) => {
            state.errorOnCustomerUpload = null;
        },
        resetCustomerDownloadState: (state) => {
            state.xlsxUrl = null;
        },
    },
    extraReducers: (builder) => {
        // customer list
        builder.addCase(getCustomerList.pending, (state) => {
            state.loading = true;
        }).addCase(getCustomerList.fulfilled, (state, action) => {
            state.loading = false;
            state.data = action.payload.customers;
            state.totalItems = action.payload.total;
        }).addCase(getCustomerList.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // change customer status
        builder.addCase(changeCustomerStatus.pending, (state) => {
            state.loading = true;
        }).addCase(changeCustomerStatus.fulfilled, (state, action) => {
            state.loading = false;
            state.successOnCustomerStatusChange = 'Customer status changed successfully!';
            state.data = replaceStatusChangedItem(
                state.data,
                action.payload.id,
                action.payload.status,
            );
        }).addCase(changeCustomerStatus.rejected, (state, action) => {
            state.loading = false;
            state.errorOnCustomerStatusChange = action.payload;
        });

        // Handle deleting customer
        builder.addCase(deleteCustomer.fulfilled, (state, action) => {
            state.data = removeSelectedItem(state.data, action.payload.customerId);
            state.totalItems -= 1;
        });

        // download customer data
        builder.addCase(downloadCustomerData.pending, (state) => {
            state.loading = true;
        }).addCase(downloadCustomerData.fulfilled, (state, action) => {
            state.loading = false;
            state.xlsxUrl = action.payload;
            if (action.meta.arg.download_type === 'mismatch') {
                state.mismatch_file = null;
            }
        }).addCase(downloadCustomerData.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // upload customer data
        builder.addCase(uploadCustomerData.pending, (state) => {
            state.loading = true;
        }).addCase(uploadCustomerData.fulfilled, (state, action) => {
            state.loading = false;
            state.mismatch_file = action.payload.mismatch_file;
            state.successOnCustomerUpload = action.payload.mismatch_file === null
                ? 'All customers updated successfully!'
                : (
                    `${action.payload.updated_rows} ${action.payload.updated_rows === 0 || action.payload.updated_rows === 1 ? 'customer' : 'customers'} updated!\n`
                    + `${action.payload.mismatch_rows} ${action.payload.mismatch_rows === 0 || action.payload.mismatch_rows === 1 ? 'customer was' : 'customers were'} not updated due to mismatch!`
                );
        }).addCase(uploadCustomerData.rejected, (state, action) => {
            state.loading = false;
            state.errorOnCustomerUpload = action.payload;
        });
    },
});

export const {
    resetChangeCustomerStatusStateOnSuccess,
    resetChangeCustomerStatusStateOnError,
    resetCustomerUploadStateOnSuccess,
    resetCustomerUploadStateOnError,
    resetCustomerDownloadState,
} = customerListSlice.actions;

export default customerListSlice.reducer;
